import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * GovRouteGate — gates /gov/:country/* surveillance routes.
 *
 * Access rules (fail-closed):
 *  - Must be authenticated (ProtectedRoute upstream already enforces this).
 *  - Must be a gov or platform admin (user_is_gov_or_platform_admin RPC).
 *  - The :country param must be in the user's authorized country scopes
 *    (user_gov_country_codes RPC), unless the user is a platform admin —
 *    platform admins bypass country scope but are steered to the read-only
 *    /admin/* mirrors instead of the write-enabled Gov portal.
 *
 *  PHC CHWs, patients, sponsors, CROs, and trial site staff without a gov
 *  role are denied and bounced to `/`.
 */
export function GovRouteGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { country } = useParams();
  const countryCode = (country || '').toUpperCase();

  const gate = useQuery({
    queryKey: ['gov-route-gate', user?.id, countryCode],
    enabled: !!user?.id && !!countryCode,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ data: isGov }, { data: codes }, { data: isPlatformAdmin }] = await Promise.all([
        supabase.rpc('user_is_gov_or_platform_admin' as any),
        supabase.rpc('user_gov_country_codes' as any),
        supabase.rpc('user_has_admin_role' as any, { _user_id: user!.id }),
      ]);
      const scopes = ((codes as string[] | null) ?? []).map((c) => c.toUpperCase());
      return {
        isGov: !!isGov,
        isPlatformAdmin: !!isPlatformAdmin,
        inScope: scopes.includes(countryCode),
      };
    },
  });

  if (gate.isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  const s = gate.data;
  if (!s || !s.isGov) return <Navigate to="/" replace />;

  // Platform admins without an explicit gov country scope should use the
  // read-only /admin mirror rather than the write-enabled Gov portal.
  if (!s.inScope && s.isPlatformAdmin) {
    const mirror: Record<string, string> = {
      'command-center': '/admin/directory-onboarding-command-center',
    };
    // Best-effort steer; specific mirror pages are registered under /admin/*.
    return <Navigate to={mirror['command-center'] ?? '/admin'} replace />;
  }

  if (!s.inScope) return <Navigate to="/" replace />;

  return <>{children}</>;
}
